import { useCallback, useEffect, useState } from 'react';
import { Banner, BlockStack, Button, Text } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

type BillingEvent = { id: string; event_type: string; status: string; error: string | null; created_at: string };

export function BillingUpdates() {
  const { currentOrganization } = useAuth();
  const { fetchApi, error, loading } = useApi();
  const [events, setEvents] = useState<BillingEvent[] | null>(null);
  const orgId = currentOrganization?.id;
  const allowed = currentOrganization && ['owner', 'admin'].includes(currentOrganization.role);
  const load = useCallback(async () => {
    if (!orgId || !allowed) return;
    const result = await fetchApi<{ events: BillingEvent[] }>('/api/stripe/events', {
      headers: { 'X-Organization-Id': orgId },
    });
    if (result) setEvents(result.events);
  }, [orgId, allowed, fetchApi]);
  useEffect(() => { setEvents(null); void load(); }, [load]);
  if (!allowed) return null;
  const pending = events?.filter(event => ['pending', 'processing', 'failed'].includes(event.status));
  return <BlockStack gap="300">
    <Text as="h3" variant="headingSm">Billing updates</Text>
    {error && <Banner tone="critical">{error}</Banner>}
    <Button loading={loading} onClick={() => void load()}>Refresh billing updates</Button>
    {events && !pending?.length && <Text as="p">No pending or failed billing updates.</Text>}
    {pending?.map(event => <BlockStack key={event.id} gap="200">
      <Text as="p">{event.status === 'failed' ? 'Needs attention' : 'Waiting for an update'}: {event.event_type}</Text>
      {event.error && <Banner tone="warning">{event.error}</Banner>}
      {event.status === 'failed' && <Button disabled={loading} onClick={async () => {
        const result = await fetchApi(`/api/stripe/events/${encodeURIComponent(event.id)}/retry`, {
          method: 'POST', headers: { 'X-Organization-Id': orgId! },
        });
        if (result) await load();
      }}>Retry billing update</Button>}
    </BlockStack>)}
  </BlockStack>;
}
